import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Download, LogOut, Sparkles, User, Menu } from "lucide-react";
import { generateMisPdf, buildMisData } from "../lib/mis";
import { logout, initialsOf } from "../lib/customerSession";
import useCustomerData from "../hooks/useCustomerData";
import ConnectedSourcesPill from "./ConnectedSourcesPill";
import NotificationBell from "./NotificationBell";
import Button from "./core/Button";
import { BACKEND_URL } from "../lib/backendUrl";

function StatusDot({ ok, label }) {
  const color = ok ? "bg-status-onTrack" : "bg-status-critical";
  return (
    <span
      data-testid="supabase-status-dot"
      title={label}
      className={`inline-block h-2 w-2 rounded-full ${color}`}
    />
  );
}

export default function TopBar({ pageTitle, company, supabaseStatus, customer, offsetTop = 0, onToggleNav }) {
  const ok = supabaseStatus?.ok === true;
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // Live customer data — the MIS download uses this to render real P&L,
  // cash-flow and inventory sections.
  const { customerData, customer: customerLive } = useCustomerData();

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const handleAskAI = () => {
    const focusChat = (maxTries = 30) => {
      let tries = 0;
      const poll = setInterval(() => {
        tries += 1;
        const input = document.querySelector('[data-testid="chat-input"]');
        const card = document.querySelector('[data-testid="ai-chat-card"]');
        if (card) {
          card.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        if (input && !input.disabled) {
          clearInterval(poll);
          // small delay so smooth-scroll completes before focus
          setTimeout(() => input.focus(), 350);
        } else if (tries >= maxTries) {
          clearInterval(poll);
        }
      }, 100);
    };

    if (location.pathname !== "/executive") {
      navigate("/executive");
      // wait for page mount + data fetch (max ~3s)
      focusChat(30);
    } else {
      focusChat(20);
    }
  };

  const handleDownloadMIS = async () => {
    try {
      // Best-effort fetch integrations confidence so the report can show it.
      let integrationsConfidence = null;
      try {
        const cid = customerLive?.id || customer?.id;
        if (cid) {
          const r = await fetch(`${BACKEND_URL}/api/integrations/health`, { credentials: "include" });
          if (r.ok) {
            const j = await r.json();
            integrationsConfidence = j?.confidence || null;
          }
        }
      } catch (_e) { /* silent — confidence is optional */ }

      const data = buildMisData({
        customerData,
        customer: customerLive || customer,
        integrationsConfidence,
      });
      if (!data) {
        alert("Your data is still loading. Please wait a moment and try again, or connect Tally to enable the MIS report.");
        return;
      }
      generateMisPdf(data);
    } catch (err) {
      // Should never happen — jsPDF is bundled — but never crash the UI.
      // eslint-disable-next-line no-console
      console.error("MIS PDF generation failed", err);
      alert("Sorry, we couldn't generate the report. Please try again in a moment.");
    }
  };

  return (
    <header
      data-testid="topbar"
      className="fixed right-0 left-0 tablet:left-16 desktop:left-[230px] z-30 flex h-[44px] items-center justify-between border-b border-ink-200 bg-white px-3 desktop:px-4"
      style={{ top: offsetTop }}
    >
      <div className="flex items-center gap-2 desktop:gap-3 min-w-0">
        <button
          type="button"
          data-testid="topbar-hamburger"
          onClick={onToggleNav}
          aria-label="Toggle navigation menu"
          className="desktop:hidden inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-700 hover:bg-ink-100 transition-colors"
        >
          <Menu size={18} />
        </button>
        <h1
          data-testid="topbar-title"
          className="text-[13px] desktop:text-[14px] font-display font-semibold text-blue-800 truncate"
        >
          {pageTitle}
        </h1>
        <span className="hidden tablet:inline-flex desktop:inline-flex">
          <StatusDot
            ok={ok}
            label={supabaseStatus?.message || (ok ? "Supabase connected" : "Supabase unreachable")}
          />
        </span>
      </div>

      <div className="flex items-center gap-1.5 desktop:gap-2 shrink-0">
        <span className="hidden tablet:inline-flex desktop:inline-flex">
          <ConnectedSourcesPill
            integrationsConnected={customerData?.params?.integrations_connected}
            activeSource={customerData?.data_source}
            compact={false}
            testid="topbar-sources"
          />
        </span>

        <span
          data-testid="topbar-date-chip"
          className="hidden md:inline-flex items-center rounded-pill bg-ink-100 px-2.5 py-[3px] text-[11px] font-mono font-medium text-ink-700"
        >
          {company?.current_date || "—"}
        </span>

        <span className="hidden tablet:inline-flex desktop:inline-flex">
          <Button
            testId="topbar-download-mis"
            onClick={handleDownloadMIS}
            variant="secondary"
            size="sm"
          >
            <Download size={13} />
            <span className="hidden desktop:inline">Download MIS</span>
          </Button>
        </span>

        <Button
          testId="topbar-ask-ai"
          onClick={handleAskAI}
          variant="accent"
          size="sm"
        >
          <Sparkles size={13} />
          <span className="hidden desktop:inline">Ask C-ORE</span>
        </Button>

        {customer && <NotificationBell />}

        {customer ? (
          <div ref={menuRef} className="relative ml-1">
            <button
              data-testid="topbar-avatar"
              onClick={() => setMenuOpen((v) => !v)}
              title={customer.email || ""}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-blue-800 text-white text-[12px] font-bold hover:bg-blue-900 transition-colors"
            >
              {initialsOf(customer.name)}
            </button>
            {menuOpen && (
              <div
                data-testid="topbar-user-menu"
                className="absolute right-0 mt-2 w-44 rounded-md border border-ink-200 bg-white shadow-hover z-50"
              >
                <div className="px-3 py-2 border-b border-ink-100">
                  <div className="text-[12px] font-semibold text-ink-950 truncate">{customer.name}</div>
                  <div className="text-[11px] text-ink-500 truncate">{customer.email}</div>
                </div>
                <button
                  data-testid="topbar-menu-account"
                  onClick={() => { setMenuOpen(false); navigate("/account"); }}
                  className="w-full text-left px-3 py-2 text-[12px] text-ink-950 hover:bg-ink-50 inline-flex items-center gap-2"
                >
                  <User size={12} /> My Account
                </button>
                <button
                  data-testid="topbar-menu-signout"
                  onClick={async () => { setMenuOpen(false); await logout(); navigate("/login"); }}
                  className="w-full text-left px-3 py-2 text-[12px] text-ink-950 hover:bg-ink-50 inline-flex items-center gap-2"
                >
                  <LogOut size={12} /> Sign Out
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="ml-1 inline-flex items-center gap-3">
            <button
              data-testid="topbar-login-link"
              onClick={() => navigate("/login")}
              className="text-[12px] font-medium text-ink-500 hover:text-ink-950 transition-colors"
            >
              Log in
            </button>
            <Button testId="topbar-get-started" onClick={() => navigate("/signup")} variant="primary" size="sm">
              Get Started
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
